/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { AuthService } from 'app/um/services/auth-service';
import { Toaster } from 'app/shared/lib/toaster';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { getReferences, getReference } from 'app/shared/models/essette-types';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AuthChangeEvent } from 'app/um/lib/events';
import { safe, deepClone } from 'app/shared/lib/utilities';
import { NavigationInstruction, RouteConfig } from 'aurelia-router';
import { UseContainerCache } from 'app/shared/lib/cache/use-container-cache';
import { IRoutingCache, createRoutingCache } from 'app/um/cache/routing-cache';
import { EssTableCustomElement } from 'app/shared/elements/ess-table';
import { fireEvents } from 'app/shared/lib/fire-events';
import { I18N } from 'aurelia-i18n';
import { validate } from 'app/shared/elements/ess-rule';
import * as UM from 'app/um/models/essette-api';

@autoinject()
@UseContainerCache({ cacheFactory: createRoutingCache })
export class AuthRoutingPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected cacheKey = 'UM_AUTH_ROUTING_CACHE';
    declare cache: IRoutingCache;

    private authId!: string;
    protected readonly: boolean = false;

    protected selectedQueue: UM.AuthQueue | null = null;
    protected currentQueues: ReadonlyArray<UM.AuthQueueHistory> | null = null;

    protected selectedOwner: UM.AuthOwner | null = null;
    protected authOwners: ReadonlyArray<UM.AuthOwner> | null = null;

    private queueHistorySorting: Sorting = new Sorting();
    protected queueHistoryPageSize: number = 5;

    private assignmentHistorySorting: Sorting = new Sorting();
    protected assignmentHistoryPageSize: number = 5;

    protected hasAssignAuth: boolean = false;

    protected assignedToInvalid: boolean | null = null;

    private subscriptions: Array<Subscription> = [];

    protected $isAssignAuthValid: boolean = false;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $queueHistoryTable!: EssTableCustomElement<UM.AuthQueueHistory>;
    protected $queueHistoryPager!: EssPagerCustomElement;
    protected $assignmentHistoryTable!: EssTableCustomElement<UM.AuthAssignmentHistory>;
    protected $assignmentHistoryPager!: EssPagerCustomElement;
    protected $assignAuthForm!: IForm;
    protected $element!: Element;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly context: AuthPageContext,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate(params: { authId: string }, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<boolean> {
        if(!params.authId) { throw new Error('Missing or invalid authId parameter'); }

        await this.accountContext.load();
        return this.accountContext.hasRouteAccess(navInstruction.config.name!);
    }

    protected async activate(params: { authId: string }): Promise<void> {
        if(this.authId !== params.authId) { this.authId = params.authId; }

        // if cache exists but auth ID is now different, reload
        if(this.cache && this.cache.authId !== this.authId) await this.reload();

        this.checkPermissions();

        this.subscriptions.push(
            this.events.subscribe(AuthChangeEvent, (e: AuthChangeEvent) => this.handleAuthChangeEvent(e))
        );
    }

    protected bind(): void {
        if(!this.cache.isFresh('authOwners') && this.hasAssignAuth) this.loadAuthOwners();
        if(!this.cache.isFresh('authQueues')) this.loadQueues();
        if(!this.cache.isFresh('assignmentHistory') || this.cache.authId !== this.authId) {
            this.loadAssignmentHistory();
        } else {
            if(!!this.cache.assignmentHistorySorting) this.assignmentHistorySorting = this.cache.assignmentHistorySorting;
            this.$assignmentHistoryPager.page = this.cache.assignmentHistoryPage || 0;
        }
        if(!this.cache.isFresh('queueHistory') || this.cache.authId !== this.authId) {
            this.loadQueueHistory();
        } else {
            if(!!this.cache.queueHistorySorting) this.queueHistorySorting = this.cache.queueHistorySorting;
            this.$queueHistoryPager.page = this.cache.queueHistoryPage || 0;
        }
    }

    protected attached(): void {
        if(this.$assignmentHistoryTable && this.assignmentHistorySorting.entries.length > 0) {
            this.$assignmentHistoryTable.applySorting(this.assignmentHistorySorting);
        }
        if(this.$queueHistoryTable && this.queueHistorySorting.entries.length > 0) {
            this.$queueHistoryTable.applySorting(this.queueHistorySorting);
        }
    }

    protected deactivate(): void {
        this.subscriptions.forEach((s) => { s.dispose(); });
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected handleAuthChangeEvent(e: AuthChangeEvent): void {
        if(e.authReloaded) {
            this.checkPermissions();
            this.reload();
        };
    }

    /** Handles the grid's sort event */
    protected async applyQueueHistorySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { queueHistorySorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            const sortDirection = direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending;
            if(sorting.entries.findIndex(entry => entry.reference === property && entry.direction === sortDirection) > -1) return;
            sorting.add({
                reference: property,
                direction: sortDirection
            });
        }
        await this.loadQueueHistory();
    }

    protected async applyAssignmentHistorySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { assignmentHistorySorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            const sortDirection = direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending;
            if(sorting.entries.findIndex(entry => entry.reference === property && entry.direction === sortDirection) > -1) return;
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.loadAssignmentHistory();
    }

    protected assignedToChanged(): void {
        this.verifyAssignment();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        const canEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        const canEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.hasAssignAuth = this.accountContext.hasPermission('UM.Auth.Assign');
        this.readonly = this.context?.authIsReadOnly(canEditAuth, canEditClosedAuth);
    }

    public async reload(): Promise<void> {
        await Promise.all([
            this.loadAuthOwners(),
            this.loadAssignmentHistory(),
            this.loadQueueHistory()
        ]);
    }

    private async verifyAssignment(): Promise<void> {
        const auth = this.context.auth;
        const assignedToId = this.selectedOwner?.id || '';
        if(!auth || !assignedToId) {
            this.assignedToInvalid = null;
            return;
        }

        try {
            this.state.beginProcessing();

            const response = await this.service.verifyAssignment(assignedToId, [getReference(auth)!]);

            // make sure "assignedTo" hasn't changed while awaiting response
            if(this.selectedOwner?.id !== assignedToId) {
                this.logger.info(this.i18n.tr('ui:messages.warnings.verificationInterruptionDiscardResults'));
            }
            else {
                this.assignedToInvalid = null;
                if(response.isSuccessful && response.result.filter && response.result.filter.items) {
                    // if no items come back, assigned to is invalid
                    this.assignedToInvalid = (response.result.filter.items.length === 0);
                }
            }
        }
        finally {
            this.state.endProcessing();
        }
    }

    protected isQueueAvailable = (queue: UM.AuthQueue): boolean => {
        // The "statuses" array is empty for "All Statuses"
        return queue.flag?.allowAnyStatus || queue.statuses?.some(s => s.id === this.context.auth.status?.id) || false;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadQueues(): Promise<void> {
        const includes = [
            'statuses'
        ];

        const fields = [
            'description',
            'flag.allowAnyStatus',
            'statuses.id'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadQueues({ all: true }, includes, false, fields);
            this.cache.authQueues = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadQueueHistory(): Promise<void> {
        if(!this.authId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.queueHistoryPageSize,
            page: this.$queueHistoryPager?.page || 0,
            sorting: this.queueHistorySorting,
            includeInactive: true
        };

        const includes = [
            'metadata.started.byUser',
            'metadata.ended.byUser',
            'queue'
        ];

        const fields = [
            'description',
            'metadata.ended.timestamp',
            'queue.description',
            'metadata.started.byUser.name.full',
            'metadata.started.byUser.description',
            'metadata.started.timestamp',
            'metadata.ended.byUser.name.full',
            'metadata.ended.byUser.description',
            'metadata.ended.timestamp'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadQueueHistoryByAuth(this.authId, options, includes, fields);
            this.cache.authId = this.authId;
            this.cache.queueHistory = response.results;
            this.cache.queueHistorySorting = this.queueHistorySorting;
            this.cache.queueHistoryResultCount = response.count || 0;

            this.currentQueues = this.cache.queueHistory.filter(q => {
                return !(q.metadata && q.metadata.ended && q.metadata.ended.timestamp)
            });
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async route(): Promise<void> {
        if(!this.selectedQueue) return;

        if(this.currentQueues && this.currentQueues.some(q => (q.queue && q.queue.id) === (this.selectedQueue && this.selectedQueue.id))) {
            this.toaster.warn().message(this.i18n.tr('um:local.auth.alreadyInTheSelectedQueue')).show();
            return;
        }

        const auth: UM.Auth = {
            id: this.authId,
            queues: getReferences([this.selectedQueue])
        };

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.routeToQueue(auth);
            isSuccessful = response.isSuccessful;
            this.selectedQueue = null;
            await this.loadQueueHistory();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.routed('um:auth.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadAuthOwners(): Promise<void> {
        const currentOwnerId = safe(() => this.context.auth!.assignedTo!.id);

        try {
            this.state.beginLoad();

            const ownerFields: string[] = ['description'];
            const query = this.service.createLoadAuthOwnersQuery({ all: true }, undefined, false, null, ownerFields);
            if(currentOwnerId) {
                query.filterIdentifier('id', currentOwnerId, 'ne');
            }
            const response = await this.service.get<UM.AuthOwner>(query);
            this.cache.authOwners = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAssignmentHistory(): Promise<void> {
        if(!this.authId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.assignmentHistoryPageSize,
            page: this.$assignmentHistoryPager?.page || 0,
            sorting: this.assignmentHistorySorting
        };

        const includes = ['user', 'metadata.started.byUser'];
        const fields = ['user.name.display', 'metadata.started.timestamp', 'metadata.started.byUser.name.display'];

        try {
            this.state.beginLoad();
            const response = await this.service.loadAssignmentHistoryByAuth(this.authId, options, includes, fields);
            this.cache.authId = this.authId;
            this.cache.assignmentHistory = response.results;
            this.cache.assignmentHistorySorting = this.assignmentHistorySorting;
            this.cache.assignmentHistoryResultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async assignAuth(): Promise<void> {
        if(!this.selectedOwner) return;

        const currentOwnerId = safe(() => this.context.auth!.assignedTo!.id);
        if(currentOwnerId === this.selectedOwner.id) {
            this.toaster.warn().message(this.i18n.tr('um:local.auth.alreadyAssignedToThatUser')).show();
            return;
        }

        const auth: UM.Auth = {
            id: this.authId,
            assignedTo: getReference(this.selectedOwner)
        };

        if(!!this.context.auth.portalQueue?.id) {
            auth.removeFromPortalQueue = true;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.assignAuth(auth);
            isSuccessful = response.isSuccessful;

            this.context.auth.assignedTo = deepClone(this.selectedOwner);
            this.selectedOwner = null;
            await this.loadAssignmentHistory();
            await this.loadAuthOwners();
            fireEvents(this.$element, ['auth-change'], { authReloaded: false, triggerRules: false });
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.assigned('um:auth.$', isSuccessful);
            this.state.endSave();
        }
    }
}
